import { createContext, ReactNode, useMemo } from 'react';

import { useProducts } from '../../../hooks/useProducts';
import {
  ProductByUuid,
  useProductsByUuid,
} from '../../../hooks/useProductsByUuid';
import {
  ProductStoryblok,
  ProductV2Storyblok,
} from '../../../types/generated-storyblok';
import { shopifyAdminIdToGraphqlId } from '../../../utils/shopifyIds';

export type CrossSellItem = {
  product: {
    artistName: string;
    availability: string;
    image: string;
    id: string;
    price: string;
    title: string;
    shopify: Product | undefined;
    storyblok: Partial<ProductType> | undefined;
  };
  variantsTitle: string;
  variants: ProductByUuid[];
};

type CrossSellContextType = {
  analytics: Record<string, unknown>;
  crossSellItems: CrossSellItem[];
  crossSellOffered: boolean;
  crossSellTitle: string;
  mainProduct?: Product;
};

export const CrossSellContext = createContext<CrossSellContextType>({
  analytics: {},
  crossSellItems: [],
  crossSellOffered: false,
  crossSellTitle: '',
  mainProduct: undefined,
});

export const CrossSellContextProvider = ({
  children,
  product,
}: {
  children: ReactNode;
  product: ProductStoryblok | ProductV2Storyblok;
}) => {
  const [{ id: mainProductId }] =
    product.component === 'product_v2'
      ? [{ id: product.shopifyId }]
      : product.id;
  const mainProductGraphqlId = shopifyAdminIdToGraphqlId(mainProductId!);

  const includingFields = ['id', 'title'];
  const excludingFields = Object.keys(product)
    .filter((field) => includingFields.includes(field) === false)
    .join(',');

  const uuids = (product.crossSellProducts || []).reduce(
    (acc, curr) => [...acc, curr.product, ...(curr.variants || [])],
    [],
  );
  const { products: shopifyProducts } = useProducts([mainProductGraphqlId]);

  const { products } = useProductsByUuid({
    excludingFields,
    // @ts-ignore
    storyblokUuids: uuids,
  });

  const items = useMemo(
    () =>
      (product.crossSellProducts || []).reduce((acc, curr) => {
        // @ts-ignore
        if (!products?.[curr.product]) return acc;

        const { shopify: mainCrossSellShopify, storyblok } =
          // @ts-ignore
          products[curr.product];

        if (!mainCrossSellShopify) return acc;

        // @ts-ignore
        const variants = (curr.variants || []).map((uuid) => products[uuid]);

        const available = variants.filter(
          (v) => v.shopify?.availability === 'Available' && v.shopify?.id,
        );

        const isAvailable = variants.length
          ? available.length > 0
          : mainCrossSellShopify.availability === 'Available';

        return [
          ...acc,
          {
            product: {
              artistName: curr.artistName || mainCrossSellShopify.artist,
              availability: isAvailable ? 'Available' : 'Sold out',
              id: mainCrossSellShopify.id,
              image: curr.image?.filename || mainCrossSellShopify.image,
              price: mainCrossSellShopify.price,
              title: curr.title || mainCrossSellShopify.title,
              shopify: mainCrossSellShopify,
              storyblok,
            },
            variantsTitle: curr.variantsTitle,
            variants,
          },
        ];
      }, []),
    [product.crossSellProducts, products],
  );

  const value = useMemo(() => {
    const available = items.filter(
      (item) => item.product.availability === 'Available',
    );
    const [mainProduct] = shopifyProducts;
    return {
      analytics: {
        'cross sell available count': available.length,
        'cross sell offered': available.length > 0,
        'cross sell products count': items.length,
        'cross sell products title': items.map((item) => item.product.title),

        'product title': mainProduct?.title,
        'product artist': mainProduct?.artist,
        'product medium': mainProduct?.medium,
        'product price': mainProduct?.price,
        'product edition size': mainProduct?.editionSize,
        'shopify product id': mainProduct?.id,
      },
      crossSellItems: items,
      crossSellOffered: available.length > 0,
      crossSellTitle: product.crossSellTitle,
      mainProduct,
    };
  }, [items, product.crossSellTitle, shopifyProducts]);

  return (
    // @ts-ignore
    <CrossSellContext.Provider value={value}>
      {children}
    </CrossSellContext.Provider>
  );
};
